import type Uppy from '@uppy/core';
import type { UppyFile } from '@uppy/core';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IMAGE_ICON_PATH } from 'constants/images.constants';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { RECEIPT_MAX_FILE_SIZE } from 'pages/Order/hooks/receiptUppy';
import { ReceiptUploaderError } from 'views/DefaultOrderView/components/DefaultUploadReceiptDialog';

type FilePreview =
  | { kind: 'image'; url: string; name: string }
  | { kind: 'file'; name: string };

// A file that breaks the restrictions (size, type) is reported by Uppy itself (uppy.info) and then thrown.
const addFileToUppy = (uppy: Uppy, file: File) => {
  try {
    uppy.addFile({
      source: 'custom',
      name: file.name,
      type: file.type,
      data: file,
    });
  } catch {
    // Already shown to the customer.
  }
};

type Props = {
  // The receipts step puts the reload button into the footer (SqLayout) in place of the submit button.
  reloadInFooter?: boolean;
};

export const SqPaymentReceiptsDispute: React.FC<Props> = ({
  reloadInFooter,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_receipts_dispute',
  });

  const {
    receiptsResult: { uppy, uppyStatus, requestUppy },
  } = useOrderContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  // Files dropped before the uploader is ready; they go in as soon as it is.
  const pendingFilesRef = useRef<File[]>([]);

  useEffect(() => {
    requestUppy();
  }, [requestUppy]);

  useEffect(() => {
    if (!uppy) {
      return undefined;
    }
    const handler = (file: UppyFile) => {
      if (!(file.data instanceof File)) {
        return;
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      if (file.type?.startsWith('image/')) {
        const url = URL.createObjectURL(file.data);
        previewUrlRef.current = url;
        setPreview({ kind: 'image', url, name: file.name });
      } else {
        setPreview({ kind: 'file', name: file.name });
      }
    };

    uppy.on('file-added', handler);
    return () => {
      uppy.off('file-added', handler);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, [uppy]);

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files) {
        return;
      }
      // The uploader loads on mount; until it is ready the picker is disabled, but a drop can still come in.
      if (!uppy) {
        pendingFilesRef.current = Array.from(files);
        return;
      }
      Array.from(files).forEach((file) => addFileToUppy(uppy, file));
    },
    [uppy],
  );

  useEffect(() => {
    if (!uppy || !pendingFilesRef.current.length) {
      return;
    }
    const files = pendingFilesRef.current;
    pendingFilesRef.current = [];
    files.forEach((file) => addFileToUppy(uppy, file));
  }, [uppy]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  if (uppyStatus === 'failed') {
    return <ReceiptUploaderError hideAction={reloadInFooter} />;
  }

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-gap-3">
      <div
        aria-busy={!uppy}
        className={cx(
          'tw-w-full tw-px-5 tw-py-4 tw-bg-payment-layout tw-rounded-2xl tw-flex tw-items-center',
          uppy ? 'tw-cursor-pointer' : 'tw-cursor-wait tw-opacity-60',
        )}
        onClick={() => uppy && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <input
          ref={inputRef}
          type="file"
          className="tw-hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <img
          src={IMAGE_ICON_PATH}
          alt={t('buttons.sq_upload')}
          className="tw-mr-4"
        />
        <span className="tw-text-md">{t('buttons.sq_upload')}</span>
      </div>
      <span className="tw-text-md tw-text-secondary">
        {t('buttons.max_file_size', {
          size: RECEIPT_MAX_FILE_SIZE / (1024 * 1024),
        })}
      </span>
      {preview && (
        <div className="tw-relative tw-mt-4 tw-w-16 tw-h-16">
          {preview.kind === 'image' ? (
            <img
              src={preview.url}
              alt={preview.name}
              className="tw-w-16 tw-h-16 tw-rounded-lg tw-object-cover"
            />
          ) : (
            <div className="tw-w-16 tw-h-16 tw-rounded-lg tw-bg-payment-layout tw-flex tw-items-center tw-justify-center">
              <span className="tw-text-xs tw-font-medium tw-text-secondary">
                {preview.name.includes('.')
                  ? `.${preview.name.split('.').pop()}`
                  : ''}
              </span>
            </div>
          )}
          <button
            type="button"
            aria-label="remove"
            className="tw-absolute -tw-top-2 -tw-right-2 tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-layout tw-border-none tw-cursor-pointer tw-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
            onClick={() => {
              uppy?.cancelAll();
              setPreview(null);
            }}
          >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 2l8 8M10 2L2 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
